import { DatePicker, Grid, GridColumn, GridRow, Input } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { Form, Formik } from 'formik';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { ensureDate } from '../../../utils/dateCalculations';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { validationSchema } from './threemaForm.schema';

const CreateThreemasMutationDocument = graphql(`
  mutation CreateThreemas($input: [ThreemaCreateInput!]!) {
    createThreemas(input: $input) {
      threemas {
        uuid
        __typename
      }
    }
  }
`);
const UpdateThreemasMutationDocument = graphql(`
  mutation UpdateThreemas($update: ThreemaUpdateInput!, $where: ThreemaWhere!) {
    updateThreemas(update: $update, where: $where) {
      threemas {
        uuid
        __typename
        apiToken
        organizationConnection {
          __typename
          edges {
            __typename
            properties {
              __typename
              notBefore
              notAfter
            }
          }
        }
      }
    }
  }
`);

const SettingsThreemaFormQueryDocument = graphql(`
  query SettingsThreemaFormData($uuid: ID!) {
    threemas(where: { uuid: $uuid }) {
      uuid
      __typename
      apiToken
      organizationConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            notBefore
            notAfter
          }
        }
      }
    }
  }
`);

type ThreemaValuesType = {
  apiToken: string | null;
  notBefore: string;
  notAfter?: string;
};

type ThreemaFormProps = { onClose: () => void };

export const ThreemaForm = ({ onClose }: ThreemaFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { organizationProducts } = useContext(OrganizationConfigContext);

  const threemaUuid = organizationProducts?.find((d) => d.__typename === 'Threema')?.uuid;

  const [{ data: queryData }] = useQuery({
    query: SettingsThreemaFormQueryDocument,
    variables: {
      uuid: threemaUuid ?? '',
    },
  });

  const currentBooking = queryData?.threemas[0];
  const currentBookingEdge = currentBooking?.organizationConnection.edges[0];

  const permissions = usePermissionChecker();

  const [, updateThreemas] = useMutation(UpdateThreemasMutationDocument);
  const [, createThreemas] = useMutation(CreateThreemasMutationDocument);

  const initialValues: ThreemaValuesType = {
    apiToken: currentBooking?.apiToken ?? '',
    notBefore: ensureDate(currentBookingEdge?.properties.notBefore ?? null).toUTCString(),
    notAfter: currentBookingEdge?.properties.notAfter
      ? ensureDate(currentBookingEdge?.properties.notAfter).toUTCString()
      : undefined,
  };

  const onSubmit = async (values: ThreemaValuesType) => {
    if (permissions?.canUpdateThreema({ uuid: organizationUuid })) {
      if (threemaUuid === undefined) {
        const resp = await createThreemas({
          input: [
            {
              apiToken: values.apiToken,
              organization: {
                connect: {
                  overwrite: true,
                  edge: {
                    notAfter: values.notAfter ?? null,
                    notBefore: values.notBefore,
                  },
                  where: { node: { uuid: organizationUuid } },
                },
              },
            },
          ],
        });

        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      } else {
        const resp = await updateThreemas({
          update: {
            apiToken: values.apiToken,
            organization: {
              update: {
                edge: {
                  notAfter: values.notAfter,
                  notBefore: values.notBefore,
                },
              },
            },
          },
          where: { uuid: threemaUuid },
        });

        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      }
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ resetForm, setFieldValue, handleChange, handleBlur, values, isSubmitting, errors }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingBottom='s'>
                <GridColumn width={6}>
                  <DatePicker
                    label={t('institution.bookedFromDate')}
                    onChange={(e) => setFieldValue('notBefore', e?.toUTCString())}
                    value={ensureDate(values.notBefore)}
                    name={'notBefore'}
                    showMonthYearDropdown
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <DatePicker
                    label={t('institution.bookedUntilDate')}
                    onChange={async (e) => await setFieldValue('notAfter', e?.toUTCString())}
                    value={values.notAfter ? ensureDate(values.notAfter) : undefined}
                    name={'notAfter'}
                    showMonthYearDropdown
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingTop='s'>
                <Input
                  className='mb-2'
                  label={t('settings.threemaWorkApiToken')}
                  name={'apiToken'}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.apiToken?.toString()}
                  error={errors.apiToken}
                />
              </GridRow>
            </Grid>

            <ModalBottomButtons
              errors={errors}
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              isLoading={isSubmitting}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
